"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader, CalendarDays, MapPin, Package, Clock, User, Mail, Phone } from "lucide-react";
export default function ScheduleACollection() {
  return <section data-section-id="223898" id="request-a-technical-quote" className="bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/5 blur-[120px]" />
      </div>

      <div className="max-w-3xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-default font-bold mb-4 tracking-tight uppercase">
            Request a Technical Quote
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default">
            Provide your site specifications for a comprehensive electrical infrastructure assessment and installation estimate.
          </p>
        </div>

        <FormProvider formKey="request_quote_form_services" formId="request_quote_form_services" sectionName="ScheduleACollection" defaultValues={{
        name: "",
        email: "",
        phone: "",
        address: "",
        collection_date: undefined,
        time_slot: "",
        bags: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true
        },
        address: {
          required: true
        },
        collection_date: {
          required: true
        },
        time_slot: {
          required: true
        },
        bags: {
          min: 100,
          max: 100000
        }
      }} submitText="Generate Quote Request" successMessage="Technical specifications received. Our engineering team is reviewing your site requirements and will issue a preliminary quote within 24 hours.">
          <CollectionForm />
        </FormProvider>
      </div>
    </section>;
}
function CollectionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="border-primary/30 bg-card/50 backdrop-blur-sm shadow-2xl rounded-none">
        <CardContent className="pt-12 pb-12 flex flex-col items-center text-center space-y-6">
          <div className="size-16 rounded-none bg-primary flex items-center justify-center text-primary-foreground mb-4">
            <Package className="size-8" />
          </div>
          <h3 className="text-2xl font-default font-bold uppercase tracking-wider">Submission Confirmed</h3>
          <p className="text-muted-foreground max-w-md font-default">
            {successMessage}
          </p>
          <Button onClick={resetForm} variant="outline" className="mt-4 rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground font-mono">
            New Technical Request
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="shadow-4xl border-border/60 bg-card/90 backdrop-blur-xl rounded-none">
      <CardHeader className="pb-8 border-b border-primary/20 bg-muted/30">
        <CardTitle className="text-xl flex items-center gap-2 font-default font-bold uppercase tracking-tight">
          <MapPin className="size-5 text-primary" />
          Site Specifications
        </CardTitle>
        <CardDescription className="font-mono text-xs uppercase tracking-widest">
          Engineering Data Input Required for Accurate Load Calculation
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-8">
        <Form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {}
            <FormField control={control} name="name" rules={{
            required: "Project Lead name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-mono text-xs uppercase">
                    <User className="size-4 text-primary" />
                    Project Lead
                  </FormLabel>
                  <FormControl>
                    <Input className="rounded-none border-border focus-visible:ring-primary focus-visible:border-primary" placeholder="Lead Engineer Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="email" rules={{
            required: "Corporate email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-mono text-xs uppercase">
                    <Mail className="size-4 text-primary" />
                    Corporate Email
                  </FormLabel>
                  <FormControl>
                    <Input className="rounded-none border-border focus-visible:ring-primary focus-visible:border-primary" type="email" placeholder="engineering@firm.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="phone" rules={{
            required: "Site contact number is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-mono text-xs uppercase">
                    <Phone className="size-4 text-primary" />
                    Site Contact
                  </FormLabel>
                  <FormControl>
                    <Input className="rounded-none border-border focus-visible:ring-primary focus-visible:border-primary" type="tel" placeholder="+44 000 000 0000" {...field} />
                  </FormControl>
                  <FormDescription className="text-[10px] font-mono">Direct line for technical clarification</FormDescription>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="bags" rules={{
            min: {
              value: 100,
              message: "Minimum 100 sq ft"
            },
            max: {
              value: 1000000,
              message: "Exceeds standard limit"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-mono text-xs uppercase">
                    <Package className="size-4 text-primary" />
                    Site Square Footage
                  </FormLabel>
                  <FormControl>
                    <Input className="rounded-none border-border focus-visible:ring-primary focus-visible:border-primary" type="number" min="100" placeholder="Total Area (sq ft)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <div className="md:col-span-2">
              <FormField control={control} name="address" rules={{
              required: "Installation address is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="flex items-center gap-2 font-mono text-xs uppercase">
                      <MapPin className="size-4 text-primary" />
                      Installation Site Address
                    </FormLabel>
                    <FormControl>
                      <textarea className="flex min-h-[100px] w-full rounded-none border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30 resize-y font-default" placeholder="Full site location and access instructions" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            {}
            <div className="md:col-span-1 flex flex-col">
              <FormField control={control} name="collection_date" rules={{
              required: "Please select a site survey date"
            }} render={({
              field
            }) => <FormItem className="flex flex-col h-full">
                    <FormLabel className="flex items-center gap-2 font-mono text-xs uppercase">
                      <CalendarDays className="size-4 text-primary" />
                      Requested Survey Date
                    </FormLabel>
                    <div className="rounded-none border border-input bg-background/50 p-3 flex-1 flex items-center justify-center">
                      <SafeHydrate><Calendar mode="single" selected={field.value} onSelect={field.onChange} disabled={date => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today;
                  }} className="pointer-events-auto" /></SafeHydrate>
                    </div>
                    <FormDescription className="text-[10px] font-mono">Earliest available engineering slot</FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>

            {}
            <div className="md:col-span-1 flex flex-col justify-start">
              <FormField control={control} name="time_slot" rules={{
              required: "Please select an estimated load"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="flex items-center gap-2 font-mono text-xs uppercase">
                      <Clock className="size-4 text-primary" />
                      Estimated Power Load
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full rounded-none border-border focus:ring-primary">
                          <SelectValue placeholder="Select Load Capacity" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-none border-border">
                        {["Standard Commercial (Up to 100kVA)", "Industrial Heavy (100kVA - 500kVA)", "High Voltage Infrastructure (500kVA+)"].map((slot, i) => <SelectItem key={i} value={slot} data-index={i} className="rounded-none focus:bg-primary/10 focus:text-foreground">
                            {slot}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />
              <div className="mt-8 p-4 bg-muted/50 border-l-4 border-primary">
                <p className="text-xs font-mono text-muted-foreground leading-relaxed">
                  <span className="text-primary font-bold">TECHNICAL ASSISTANT:</span> All installations comply with BS 7671 safety standards. Site plans can be uploaded after initial review.
                </p>
              </div>
            </div>
          </div>

          {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-none">
              <p className="text-destructive text-sm font-mono uppercase">{submitError}</p>
            </div>}

          <div className="mt-8 pt-6 border-t border-border/20 flex justify-end">
            <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[240px] bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-default font-bold uppercase tracking-widest shadow-lg">
              {isSubmitting ? <>
                  <Loader className="mr-2 size-4 animate-spin" />
                  Processing Data...
                </> : "Generate Quote Request"}
            </Button>
          </div>
        </Form>
      </CardContent>
    </Card>;
}